/**
 * Shared hook for the AI-powered "Heal with AI" action. Pulls the failed
 * build's GitHub Actions log + the project's source files, asks Claude to
 * propose minimum-diff fixes, and pushes them back to the repo (which
 * auto-triggers a new build via the push webhook).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api';

export interface HealResult {
  changed: boolean;
  summary: string;
  filesChanged?: string[];
  commitSha?: string;
  commitUrl?: string;
  cost?: number;
  model?: string;
  message: string;
}

export function useHealBuild(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post(`/builds/${projectId}/heal`);
      return res.data.data as HealResult;
    },
    onSuccess: (data) => {
      if (!data.changed) {
        toast.info(
          <div className="text-xs">
            <strong>AI heal — no changes</strong>
            <div className="mt-1 text-gray-600">{data.summary}</div>
          </div>,
          { duration: 8000 }
        );
        return;
      }
      toast.success(
        <div className="text-xs">
          <strong>AI heal applied</strong>
          <div className="mt-1 text-gray-600">{data.summary}</div>
          <div className="mt-1">
            {data.filesChanged?.length ?? 0} files · cost ${data.cost?.toFixed(4) ?? '0.00'}
          </div>
        </div>,
        { duration: 8000 }
      );
      queryClient.invalidateQueries({ queryKey: ['builds', projectId, 'latest'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'AI heal failed');
    },
  });
}
